import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import {
  ApplicationInsights,
  DistributedTracingModes,
  ITelemetryItem
} from '@microsoft/applicationinsights-web';

import { environment } from '../../../environments/environment';

/**
 * Replace anything that could be PII in a URL/path with a stable placeholder, so
 * telemetry keeps its diagnostic shape (`/employer/employees/{id}`) without ever
 * sending real employee ids, NINs, tokens, or emails to Azure. Query strings and
 * fragments (where one-time tokens live) are dropped entirely.
 */
function scrubUrl(value: string): string {
  let s = value.split('#')[0].split('?')[0];
  s = s.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '{id}'); // GUIDs
  s = s.replace(/\d{6,}/g, '{id}');                                                        // long numeric ids / NIN
  s = s.replace(/[^/\s@]+@[^/\s@]+\.[^/\s@]+/g, '{email}');                                // emails
  return s;
}

/**
 * Azure Application Insights for the SPA. Operational telemetry only (errors +
 * performance + page/route + API correlation), NOT marketing. It loads only when:
 *   1. a connection string is configured for the environment, and
 *   2. the user has granted Analytics consent (driven from AppComponent).
 *
 * All URL-bearing telemetry is PII-scrubbed before it leaves the browser. This is
 * a payroll app, so that scrubbing is the non-negotiable part.
 */
@Injectable({ providedIn: 'root' })
export class AppInsightsService {
  private readonly router = inject(Router);
  private ai: ApplicationInsights | null = null;
  /** Employer id to tag telemetry with — remembered until the SDK is ready. */
  private pendingUserId: string | null = null;

  /** Idempotent. No-op unless a connection string is configured. */
  init(): void {
    const connectionString = environment.appInsightsConnectionString;
    if (!connectionString || this.ai) { return; }

    const ai = new ApplicationInsights({
      config: {
        connectionString,
        // We track page views manually (scrubbed) instead of auto-route tracking,
        // so no raw authenticated URL is ever the page name.
        enableAutoRouteTracking: false,
        disableFetchTracking: false,          // capture API calls for end-to-end traces
        enableCorsCorrelation: true,          // stitch browser -> API (needs backend expose-headers)
        distributedTracingMode: DistributedTracingModes.W3C,
        enableUnhandledPromiseRejectionTracking: true,
        autoTrackPageVisitTime: true
      }
    });
    ai.loadAppInsights();
    ai.addTelemetryInitializer(this.dropThirdPartyNoise);
    ai.addTelemetryInitializer(this.scrub);
    this.ai = ai;

    // If a user id was set before the SDK finished loading, apply it now so
    // telemetry from this session is attributable.
    if (this.pendingUserId) {
      ai.setAuthenticatedUserContext(this.pendingUserId, undefined, true);
    }

    // Report the current route, then each navigation — always scrubbed.
    this.trackPage(this.router.url);
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.trackPage(e.urlAfterRedirects));
  }

  /** Forward a handled/unhandled error (used by the global ErrorHandler). No-op
   *  until the SDK is initialised, so nothing is sent before Analytics consent. */
  trackException(error: Error): void {
    this.ai?.trackException({ exception: error });
  }

  /** Emit a custom event with a PII-safe property bag. No-op until the SDK is
   *  initialised (Analytics consent in a configured environment). */
  trackEvent(name: string, properties?: Record<string, unknown>): void {
    this.ai?.trackEvent({ name }, properties);
  }

  /** Tag all telemetry with the employer id (an opaque GUID — not a name or
   *  email, so it stays PII-safe) so funnels can be split per employer. Pass null
   *  to clear on sign-out. Safe to call before init; applied once the SDK loads. */
  setUser(id: string | null): void {
    this.pendingUserId = id;
    if (!this.ai) { return; }
    if (id) { this.ai.setAuthenticatedUserContext(id, undefined, true); }
    else    { this.ai.clearAuthenticatedUserContext(); }
  }

  private trackPage(url: string): void {
    const name = scrubUrl(url.split(/[?#]/)[0] || '/');
    this.ai?.trackPageView({ name, uri: name });
  }

  /** Hostnames whose telemetry is pure third-party marketing noise, not ours. */
  private static readonly THIRD_PARTY =
    /google\.com|google-analytics\.com|googletagmanager\.com|googleadservices|doubleclick|facebook\.(com|net)|clarity\.ms/i;

  /** Drop third-party noise before it buries our own signal:
   *  - Cross-origin "Script error." exceptions — when a script from another origin
   *    throws, the browser strips the message/stack to the bare phrase "Script
   *    error." (the SDK reports it as `ErrorEvent: Script error.` with line/col 0),
   *    so they carry zero diagnostic value. Our own bundles are same-origin and
   *    report real messages, so a genuine app error never reads "script error".
   *  - Dependency calls to Google/Meta ad + analytics endpoints (the gtag/pixel
   *    beacon flood). These aren't our API and only clutter the dependency view.
   *  Returning false suppresses the item. */
  private readonly dropThirdPartyNoise = (item: ITelemetryItem): boolean => {
    if (item.baseType === 'ExceptionData') {
      const bd = item.baseData as { exceptions?: Array<{ message?: string; typeName?: string }>; message?: string } | undefined;
      const ex = bd?.exceptions?.[0];
      // Match the phrase anywhere: the SDK prefixes it, e.g. "ErrorEvent: Script error."
      const text = `${ex?.message ?? ''} ${ex?.typeName ?? ''} ${bd?.message ?? ''}`;
      if (/script error/i.test(text)) { return false; }
    }
    if (item.baseType === 'RemoteDependencyData') {
      const bd = item.baseData as { target?: string; name?: string } | undefined;
      const where = `${bd?.target ?? ''} ${bd?.name ?? ''}`;
      if (AppInsightsService.THIRD_PARTY.test(where)) { return false; }
    }
    return true;
  };

  /** Scrub URL/path fields on every telemetry item before it's sent. */
  private readonly scrub = (item: ITelemetryItem): boolean => {
    const bd = item.baseData as Record<string, unknown> | undefined;
    if (bd) {
      for (const key of ['uri', 'refUri', 'name', 'target', 'data']) {
        const v = bd[key];
        if (typeof v === 'string' && v) { bd[key] = scrubUrl(v); }
      }
    }
    return true;
  };
}
